import type {
  ChangeEvent,
  FormEventHandler,
  MouseEvent,
  ReactNode,
} from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { IconType } from 'react-icons';
import {
  FaAddressCard,
  FaBalanceScale,
  FaBolt,
  FaBriefcase,
  FaCheck,
  FaClock,
  FaCubes,
  FaFileInvoiceDollar,
  FaGavel,
  FaHandsHelping,
  FaHeartbeat,
  FaHome,
  FaPiggyBank,
  FaShieldAlt,
  FaUserFriends,
  FaUniversity,
} from 'react-icons/fa';
import {
  z,
  US_STATE_CODES,
  US_PHONE_NUMBER_REGEX,
  US_POSTAL_CODE_REGEX,
  type FinancialPOAIntake,
  type FinancialPOAPowers,
  type USStateCode,
} from '@bedside-docs/contracts';
import styles from './FinancialPoaForm.module.scss';
import { FinancialPoaFormContext } from './FinancialPoaFormContext';
import GoogleAddressInput from '../../components/GoogleAddressInput';
import {
  type ParsedPlaceAddress,
  loadGoogleMapsPlacesScript,
} from '../../utils/googlePlaces';

const STORAGE_KEY = 'poa.financial.draft.v2';
const DRAFT_VERSION = 2;

type StateCode = USStateCode;

const STATE_LABELS: Record<StateCode, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

const STATE_OPTIONS: ReadonlyArray<{ value: StateCode; label: string }> =
  US_STATE_CODES.map((code) => ({
    value: code,
    label: STATE_LABELS[code],
  }));

const isStateCode = (value: unknown): value is StateCode =>
  typeof value === 'string' &&
  (US_STATE_CODES as readonly string[]).includes(value as string);

type AddressFormValues = {
  line1: string;
  line2: string;
  city: string;
  state: StateCode;
  postalCode: string;
};

type PrincipalInfoFormValues = {
  fullName: string;
  address: AddressFormValues;
  phone: string;
  email: string;
};

type SuccessorAgentFormValues = {
  fullName: string;
  address: AddressFormValues;
  phone: string;
  email: string;
};

type AgentInfoFormValues = {
  agentFullName: string;
  agentAddress: AddressFormValues;
  agentPhone: string;
  agentEmail: string;
  includeSuccessorAgents: boolean;
  successorAgents: SuccessorAgentFormValues[];
};

type PowerKey = keyof FinancialPOAPowers;

type AuthorityGrantFormValues = {
  powers: FinancialPOAPowers;
  specialInstructions: string;
};

type EffectiveDateOption = 'immediate' | 'upon_incapacity';

type EffectiveAndTerminationFormValues = {
  effectiveDate: EffectiveDateOption | '';
  terminationConditions: string;
};

type FormValues = {
  principalInfo: PrincipalInfoFormValues;
  agentInfo: AgentInfoFormValues;
  authorityGrant: AuthorityGrantFormValues;
  effectiveAndTermination: EffectiveAndTerminationFormValues;
};

type StoredDraft = {
  version: number;
  formValues: FormValues;
  intake?: FinancialPOAIntake;
};

type FieldName = string;

const createEmptyAddress = (): AddressFormValues => ({
  line1: '',
  line2: '',
  city: '',
  state: 'ME',
  postalCode: '',
});

const createEmptySuccessorAgent = (): SuccessorAgentFormValues => ({
  fullName: '',
  address: createEmptyAddress(),
  phone: '',
  email: '',
});

const MAX_SUCCESSOR_AGENTS = 2;

const clampSuccessorAgents = (
  agents: SuccessorAgentFormValues[],
): SuccessorAgentFormValues[] => agents.slice(0, MAX_SUCCESSOR_AGENTS);

const defaultPowers: FinancialPOAPowers = {
  realEstateTransactions: false,
  tangiblePersonalPropertyTransactions: false,
  bankingAndFinancialInstitutionTransactions: false,
  businessOperatingTransactions: false,
  insuranceAndAnnuityTransactions: false,
  estateTrustAndBeneficiaryTransactions: false,
  claimsAndLitigation: false,
  personalAndFamilyMaintenance: false,
  retirementPlanTransactions: false,
  taxMatters: false,
};

const defaultValues: FormValues = {
  principalInfo: {
    fullName: '',
    address: createEmptyAddress(),
    phone: '',
    email: '',
  },
  agentInfo: {
    agentFullName: '',
    agentAddress: createEmptyAddress(),
    agentPhone: '',
    agentEmail: '',
    includeSuccessorAgents: false,
    successorAgents: [],
  },
  authorityGrant: {
    powers: defaultPowers,
    specialInstructions: '',
  },
  effectiveAndTermination: {
    effectiveDate: '',
    terminationConditions: '',
  },
};

const createDefaultValues = (): FormValues => ({
  principalInfo: {
    ...defaultValues.principalInfo,
    address: { ...defaultValues.principalInfo.address },
  },
  agentInfo: {
    ...defaultValues.agentInfo,
    agentAddress: { ...defaultValues.agentInfo.agentAddress },
    successorAgents: defaultValues.agentInfo.successorAgents.map((agent) => ({
      ...agent,
      address: { ...agent.address },
    })),
  },
  authorityGrant: {
    powers: { ...defaultValues.authorityGrant.powers },
    specialInstructions: defaultValues.authorityGrant.specialInstructions,
  },
  effectiveAndTermination: { ...defaultValues.effectiveAndTermination },
});

const hydrateAddress = (value: unknown): AddressFormValues => {
  const base = createEmptyAddress();

  if (typeof value === 'string') {
    return { ...base, line1: value };
  }

  if (value && typeof value === 'object') {
    const candidate = value as Partial<AddressFormValues>;
    return {
      line1: typeof candidate.line1 === 'string' ? candidate.line1 : base.line1,
      line2: typeof candidate.line2 === 'string' ? candidate.line2 : base.line2,
      city: typeof candidate.city === 'string' ? candidate.city : base.city,
      state: isStateCode(candidate.state) ? candidate.state : base.state,
      postalCode:
        typeof candidate.postalCode === 'string'
          ? candidate.postalCode
          : base.postalCode,
    };
  }

  return base;
};

const baseFieldNames: FieldName[] = [
  'principalInfo.fullName',
  'principalInfo.address.line1',
  'principalInfo.address.line2',
  'principalInfo.address.city',
  'principalInfo.address.state',
  'principalInfo.address.postalCode',
  'principalInfo.phone',
  'principalInfo.email',
  'agentInfo.agentFullName',
  'agentInfo.agentAddress.line1',
  'agentInfo.agentAddress.line2',
  'agentInfo.agentAddress.city',
  'agentInfo.agentAddress.state',
  'agentInfo.agentAddress.postalCode',
  'agentInfo.agentPhone',
  'agentInfo.agentEmail',
  'agentInfo.successorAgents',
  'authorityGrant.powers',
  'authorityGrant.specialInstructions',
  'effectiveAndTermination.effectiveDate',
  'effectiveAndTermination.terminationConditions',
];

type StepDefinitionId =
  | 'principal_info'
  | 'agent_info'
  | 'authority_grant'
  | 'effective_and_termination';

type StepDefinition = {
  id: StepDefinitionId;
  label: string;
  title: string;
  description: string;
  preview: ReadonlyArray<string>;
};

const stepDefinitions = [
  {
    id: 'principal_info',
    label: 'About You',
    title: 'Identify the Principal',
    description:
      'Share your contact details so the document clearly identifies who is granting this power of attorney.',
    preview: [
      'Your full legal name and current mailing address',
      'A phone number and email address you actively use',
    ],
  },
  {
    id: 'agent_info',
    label: 'Your Agent',
    title: 'Who can act for you?',
    description:
      'Provide contact information for the trusted person who will make financial decisions on your behalf.',
    preview: [
      'Your chosen agent’s full name and contact information',
      'Details for any backup agents you want to name',
    ],
  },
  {
    id: 'authority_grant',
    label: 'Agent’s Authority',
    title: 'Choose the powers to grant',
    description:
      'Select the financial responsibilities your agent may handle. You can choose as many areas as you need.',
    preview: [
      'A sense of the financial tasks you want your agent to manage',
      'Any limits or special instructions you’d like to include',
    ],
  },
  {
    id: 'effective_and_termination',
    label: 'Timing & Limits',
    title: 'Decide when the authority applies',
    description:
      'Tell us when the agent’s authority should begin and whether you want to limit how long it lasts.',
    preview: [
      'When the power should begin (immediately or upon incapacity)',
      'Any conditions that should end the authority',
    ],
  },
] as const satisfies ReadonlyArray<StepDefinition>;

type StepId = (typeof stepDefinitions)[number]['id'];

type StepCorrectionSummary = {
  stepId: StepId;
  stepLabel: string;
  items: Array<{ field: FieldName; label: string; message: string }>;
};

const stepFieldGroups: Record<StepId, (formValues: FormValues) => FieldName[]> = {
  principal_info: () => [
    'principalInfo.fullName',
    'principalInfo.address.line1',
    'principalInfo.address.city',
    'principalInfo.address.state',
    'principalInfo.address.postalCode',
    'principalInfo.phone',
    'principalInfo.email',
  ],
  agent_info: (formValues) => {
    const fields: FieldName[] = [
      'agentInfo.agentFullName',
      'agentInfo.agentAddress.line1',
      'agentInfo.agentAddress.city',
      'agentInfo.agentAddress.state',
      'agentInfo.agentAddress.postalCode',
      'agentInfo.agentPhone',
      'agentInfo.agentEmail',
      'agentInfo.successorAgents',
    ];

    if (formValues.agentInfo.includeSuccessorAgents) {
      formValues.agentInfo.successorAgents.forEach((_, index) => {
        fields.push(`agentInfo.successorAgents.${index}.fullName`);
        fields.push(`agentInfo.successorAgents.${index}.address.line1`);
        fields.push(`agentInfo.successorAgents.${index}.address.city`);
        fields.push(`agentInfo.successorAgents.${index}.address.state`);
        fields.push(`agentInfo.successorAgents.${index}.address.postalCode`);
        fields.push(`agentInfo.successorAgents.${index}.phone`);
        fields.push(`agentInfo.successorAgents.${index}.email`);
      });
    }

    return fields;
  },
  authority_grant: () => ['authorityGrant.powers'],
  effective_and_termination: () => [
    'effectiveAndTermination.effectiveDate',
    'effectiveAndTermination.terminationConditions',
  ],
};

const fieldLabelMap: Partial<Record<FieldName, string>> = {
  'principalInfo.fullName': 'Principal full legal name',
  'principalInfo.address.line1': 'Principal street address',
  'principalInfo.address.line2': 'Principal address line 2',
  'principalInfo.address.city': 'Principal city',
  'principalInfo.address.state': 'Principal state',
  'principalInfo.address.postalCode': 'Principal ZIP code',
  'principalInfo.phone': 'Principal phone number',
  'principalInfo.email': 'Principal email address',
  'agentInfo.agentFullName': 'Agent full legal name',
  'agentInfo.agentAddress.line1': 'Agent street address',
  'agentInfo.agentAddress.line2': 'Agent address line 2',
  'agentInfo.agentAddress.city': 'Agent city',
  'agentInfo.agentAddress.state': 'Agent state',
  'agentInfo.agentAddress.postalCode': 'Agent ZIP code',
  'agentInfo.agentPhone': 'Agent phone number',
  'agentInfo.agentEmail': 'Agent email address',
  'agentInfo.successorAgents': 'Successor agent details',
  'authorityGrant.powers': 'Agent powers',
  'authorityGrant.specialInstructions': 'Special instructions',
  'effectiveAndTermination.effectiveDate': 'Effective date choice',
  'effectiveAndTermination.terminationConditions':
    'End date or conditions',
};

const successorFieldLabelMap: Record<string, string> = {
  fullName: 'full legal name',
  'address.line1': 'street address',
  'address.line2': 'address line 2',
  'address.city': 'city',
  'address.state': 'state',
  'address.postalCode': 'ZIP code',
  phone: 'phone number',
  email: 'email address',
};

const getFieldLabel = (field: FieldName): string => {
  const directLabel = fieldLabelMap[field];
  if (directLabel) {
    return directLabel;
  }

  const successorMatch = field.match(
    /^agentInfo\.successorAgents\.(\d+)\.(.+)$/,
  );

  if (successorMatch) {
    const index = Number(successorMatch[1]) + 1;
    const remainder = successorMatch[2];
    const suffix = successorFieldLabelMap[remainder];
    if (suffix) {
      return `Successor agent ${index} ${suffix}`;
    }

    const addressMatch = remainder.match(/^address\.(.+)$/);
    if (addressMatch) {
      const addressKey = `address.${addressMatch[1]}`;
      const addressLabel = successorFieldLabelMap[addressKey];
      if (addressLabel) {
        return `Successor agent ${index} ${addressLabel}`;
      }
    }

    return `Successor agent ${index} detail`;
  }

  return field;
};

type FocusRequest = {
  stepId: StepId;
  selector: string;
};

const isFocusRequest = (value: unknown): value is FocusRequest =>
  Boolean(
    value &&
      typeof value === 'object' &&
      'stepId' in value &&
      'selector' in value &&
      typeof (value as { stepId?: unknown }).stepId === 'string' &&
      typeof (value as { selector?: unknown }).selector === 'string',
  );

const getAllFieldNames = (formValues: FormValues): FieldName[] => {
  const successorFields = formValues.agentInfo.successorAgents.flatMap(
    (_, index) => [
      `agentInfo.successorAgents.${index}.fullName`,
      `agentInfo.successorAgents.${index}.address.line1`,
      `agentInfo.successorAgents.${index}.address.line2`,
      `agentInfo.successorAgents.${index}.address.city`,
      `agentInfo.successorAgents.${index}.address.state`,
      `agentInfo.successorAgents.${index}.address.postalCode`,
      `agentInfo.successorAgents.${index}.phone`,
      `agentInfo.successorAgents.${index}.email`,
    ]
  );

  return [...baseFieldNames, ...successorFields];
};

const OptionalIndicator = () => (
  <span className={styles.optionalIndicator}>Optional</span>
);

type SectionCardProps = {
  title: string;
  description?: string;
  icon?: IconType;
  children: ReactNode;
};

const SectionCard = ({ title, description, icon: Icon, children }: SectionCardProps) => (
  <section className={styles.stepSection} data-expanded="true">
    <header className={styles.sectionHeader}>
      {Icon && (
        <span className={styles.sectionIcon} aria-hidden="true">
          <Icon />
        </span>
      )}
      <div>
        <h3 className={styles.sectionHeading}>{title}</h3>
        {description && (
          <p className={styles.sectionDescription}>{description}</p>
        )}
      </div>
    </header>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

type ContactSectionProps = Omit<SectionCardProps, 'icon'> & {
  icon?: IconType;
};

const ContactSection = ({ icon = FaAddressCard, ...props }: ContactSectionProps) => (
  <SectionCard {...props} icon={icon} />
);

type AddressGroupProps = Omit<SectionCardProps, 'icon'> & {
  icon?: IconType;
};

const AddressGroup = ({ icon = FaHome, ...props }: AddressGroupProps) => (
  <SectionCard {...props} icon={icon} />
);

type PowerOption = {
  key: PowerKey;
  label: string;
  description: string;
  Icon: IconType;
};

const powerOptions: PowerOption[] = [
  {
    key: 'realEstateTransactions',
    label: 'Manage real estate',
    description: 'Buy, sell, mortgage, or manage property like your home or land.',
    Icon: FaHome,
  },
  {
    key: 'tangiblePersonalPropertyTransactions',
    label: 'Care for personal property',
    description: 'Handle vehicles, equipment, and other valuable belongings.',
    Icon: FaCubes,
  },
  {
    key: 'bankingAndFinancialInstitutionTransactions',
    label: 'Work with banks & credit unions',
    description:
      'Access accounts, move money, and manage day-to-day financial transactions.',
    Icon: FaUniversity,
  },
  {
    key: 'businessOperatingTransactions',
    label: 'Run business interests',
    description: 'Make operational decisions or sign contracts for any businesses you own.',
    Icon: FaBriefcase,
  },
  {
    key: 'insuranceAndAnnuityTransactions',
    label: 'Maintain insurance & annuities',
    description: 'Update policies, pay premiums, and manage annuity contracts.',
    Icon: FaShieldAlt,
  },
  {
    key: 'estateTrustAndBeneficiaryTransactions',
    label: 'Manage estate or trust matters',
    description: 'Handle trusts, beneficiary designations, and estate-related paperwork.',
    Icon: FaBalanceScale,
  },
  {
    key: 'claimsAndLitigation',
    label: 'Handle legal claims',
    description: 'Pursue lawsuits, settlements, or defend against claims on your behalf.',
    Icon: FaGavel,
  },
  {
    key: 'personalAndFamilyMaintenance',
    label: 'Provide personal support',
    description: 'Use funds for everyday needs of you or your dependents.',
    Icon: FaHandsHelping,
  },
  {
    key: 'retirementPlanTransactions',
    label: 'Manage retirement accounts',
    description: 'Handle pensions, IRAs, and any other retirement benefits.',
    Icon: FaPiggyBank,
  },
  {
    key: 'taxMatters',
    label: 'Take care of taxes',
    description: 'Prepare, sign, and file tax forms or respond to audits.',
    Icon: FaFileInvoiceDollar,
  },
];

type EffectiveDateOptionDefinition = {
  value: EffectiveDateOption;
  label: string;
  description: string;
  Icon: IconType;
};

const effectiveDateOptions: EffectiveDateOptionDefinition[] = [
  {
    value: 'immediate',
    label: 'Right away',
    description: 'Your agent can use this document immediately after it is signed.',
    Icon: FaBolt,
  },
  {
    value: 'upon_incapacity',
    label: 'Only if you are incapacitated',
    description:
      'Authority begins if you become unable to make decisions for yourself.',
    Icon: FaHeartbeat,
  },
];

const addressSchema = z.object({
  line1: z.string().min(1, 'Street address is required'),
  line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.enum(US_STATE_CODES),
  postalCode: z
    .string()
    .regex(US_POSTAL_CODE_REGEX, 'Enter a valid ZIP code'),
});

const successorAgentSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  dateOfBirth: z.string().optional(),
  address: addressSchema,
  phone: z
    .string()
    .regex(US_PHONE_NUMBER_REGEX, 'Enter a valid phone number'),
  email: z.string().email('Enter a valid email').optional(),
});

const financialPoaSchema = z.object({
  principalInfo: z.object({
    fullName: z.string().min(1, 'Full name is required'),
    dateOfBirth: z.string().optional(),
    address: addressSchema,
    phone: z
      .string()
      .regex(US_PHONE_NUMBER_REGEX, 'Enter a valid phone number'),
    email: z.string().email('Enter a valid email').optional(),
  }),
  agentInfo: z
    .object({
      agentFullName: z.string().min(1, 'Full name is required'),
      agentAddress: addressSchema,
      agentPhone: z
        .string()
        .regex(US_PHONE_NUMBER_REGEX, 'Enter a valid phone number'),
      agentEmail: z.string().email('Enter a valid email').optional(),
      includeSuccessorAgents: z.boolean(),
      successorAgents: z.array(successorAgentSchema),
    })
    .superRefine((data, ctx) => {
      if (data.includeSuccessorAgents && data.successorAgents.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Add at least one successor agent or turn off the successor agent option.',
          path: ['successorAgents'],
        });
      }
    }),
  authorityGrant: z
    .object({
      powers: z.object({
        realEstateTransactions: z.boolean(),
        tangiblePersonalPropertyTransactions: z.boolean(),
        bankingAndFinancialInstitutionTransactions: z.boolean(),
        businessOperatingTransactions: z.boolean(),
        insuranceAndAnnuityTransactions: z.boolean(),
        estateTrustAndBeneficiaryTransactions: z.boolean(),
        claimsAndLitigation: z.boolean(),
        personalAndFamilyMaintenance: z.boolean(),
        retirementPlanTransactions: z.boolean(),
        taxMatters: z.boolean(),
      }),
      specialInstructions: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const hasSelection = Object.values(data.powers).some(Boolean);
      if (!hasSelection) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select at least one power to grant your agent.',
          path: ['powers'],
        });
      }
    }),
  effectiveAndTermination: z.object({
    effectiveDate: z.enum(['immediate', 'upon_incapacity'], {
      errorMap: () => ({
        message: 'Select when the document should become effective.',
      }),
    }),
    terminationConditions: z.string().optional(),
  }),
  delivery: z
    .object({
      sendToAgent: z.boolean(),
      agentEmail: z.string().email('Enter a valid email').optional(),
    })
    .optional(),
});

type FinancialPoaData = z.infer<typeof financialPoaSchema>;

const normalizeValues = (values: FormValues): FinancialPoaData => {
  const normalizeOptional = (value: string) => {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  const normalizeRequired = (value: string) => value.trim();

  const normalizeAddress = (address: AddressFormValues) => {
    const normalized: {
      line1: string;
      city: string;
      state: StateCode;
      postalCode: string;
      line2?: string;
    } = {
      line1: normalizeRequired(address.line1),
      city: normalizeRequired(address.city),
      state: address.state,
      postalCode: normalizeRequired(address.postalCode),
    };

    const line2 = normalizeOptional(address.line2);
    if (line2) {
      normalized.line2 = line2;
    }

    return normalized;
  };

  const successorAgents = values.agentInfo.includeSuccessorAgents
    ? values.agentInfo.successorAgents.map((agent) => ({
        fullName: normalizeRequired(agent.fullName),
        address: normalizeAddress(agent.address),
        phone: normalizeRequired(agent.phone),
        email: normalizeOptional(agent.email),
      }))
    : [];

  return {
    principalInfo: {
      fullName: normalizeRequired(values.principalInfo.fullName),
      address: normalizeAddress(values.principalInfo.address),
      phone: normalizeRequired(values.principalInfo.phone),
      email: normalizeOptional(values.principalInfo.email),
    },
    agentInfo: {
      agentFullName: normalizeRequired(values.agentInfo.agentFullName),
      agentAddress: normalizeAddress(values.agentInfo.agentAddress),
      agentPhone: normalizeRequired(values.agentInfo.agentPhone),
      agentEmail: normalizeOptional(values.agentInfo.agentEmail),
      includeSuccessorAgents: values.agentInfo.includeSuccessorAgents,
      successorAgents,
    },
    authorityGrant: {
      powers: { ...values.authorityGrant.powers },
      specialInstructions: normalizeOptional(
        values.authorityGrant.specialInstructions
      ),
    },
    effectiveAndTermination: {
      effectiveDate: (() => {
        const { effectiveDate } = values.effectiveAndTermination;
        if (
          effectiveDate === 'immediate' ||
          effectiveDate === 'upon_incapacity'
        ) {
          return effectiveDate;
        }
        throw new Error('effective_date_not_selected');
      })(),
      terminationConditions: normalizeOptional(
        values.effectiveAndTermination.terminationConditions
      ),
    },
  };
};

const toContact = (
  contact: {
    fullName: string;
    address: FinancialPoaData['principalInfo']['address'];
    phone: string;
    email?: string;
    dateOfBirth?: string;
  },
): FinancialPOAIntake['principal'] => ({
  fullName: contact.fullName,
  address: { ...contact.address },
  phone: contact.phone,
  ...(contact.email ? { email: contact.email } : {}),
  ...(contact.dateOfBirth ? { dateOfBirth: contact.dateOfBirth } : {}),
});

const mapNormalizedToFinancialPoaIntake = (
  data: FinancialPoaData,
): FinancialPOAIntake => ({
  principal: toContact({
    fullName: data.principalInfo.fullName,
    address: data.principalInfo.address,
    phone: data.principalInfo.phone,
    email: data.principalInfo.email,
    dateOfBirth: data.principalInfo.dateOfBirth,
  }),
  agent: toContact({
    fullName: data.agentInfo.agentFullName,
    address: data.agentInfo.agentAddress,
    phone: data.agentInfo.agentPhone,
    email: data.agentInfo.agentEmail,
  }),
  successorAgents: data.agentInfo.successorAgents.map((successor) =>
    toContact({
      fullName: successor.fullName,
      address: successor.address,
      phone: successor.phone,
      email: successor.email,
      dateOfBirth: successor.dateOfBirth,
    }),
  ),
  powers: { ...data.authorityGrant.powers },
  ...(data.authorityGrant.specialInstructions
    ? { specialInstructions: data.authorityGrant.specialInstructions }
    : {}),
  effectiveness: {
    type: data.effectiveAndTermination.effectiveDate,
    ...(data.effectiveAndTermination.terminationConditions
      ? {
          terminationConditions:
            data.effectiveAndTermination.terminationConditions,
        }
      : {}),
  },
  ...(data.delivery
    ? {
        delivery: {
          sendToAgent: data.delivery.sendToAgent,
          ...(data.delivery.agentEmail
            ? { agentEmail: data.delivery.agentEmail }
            : {}),
        },
      }
    : {}),
});


const FinancialPoaForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const focusRequestRef = useRef<FocusRequest | null>(null);
  const stepContainerRef = useRef<HTMLElement | null>(null);
  const hasInitializedStepRef = useRef(false);
  const [values, setValues] = useState<FormValues>(() => {
    if (typeof window === 'undefined') {
      return createDefaultValues();
    }

    const stored = window.localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return createDefaultValues();
    }

    try {
      const parsedValue = JSON.parse(stored) as unknown;
      const formValuesCandidate =
        parsedValue &&
        typeof parsedValue === 'object' &&
        'formValues' in parsedValue &&
        parsedValue.formValues &&
        typeof parsedValue.formValues === 'object'
          ? (parsedValue.formValues as Partial<FormValues>)
          : (parsedValue as Partial<FormValues>);

      return {
        principalInfo: {
          ...defaultValues.principalInfo,
          ...formValuesCandidate.principalInfo,
          address: hydrateAddress(formValuesCandidate.principalInfo?.address),
        },
        agentInfo: {
          ...defaultValues.agentInfo,
          ...formValuesCandidate.agentInfo,
          agentAddress: hydrateAddress(
            formValuesCandidate.agentInfo?.agentAddress,
          ),
          successorAgents: Array.isArray(
            formValuesCandidate.agentInfo?.successorAgents,
          )
            ? clampSuccessorAgents(
                formValuesCandidate.agentInfo.successorAgents.map(
                  (agentCandidate) => {
                    const candidate =
                      agentCandidate as Partial<SuccessorAgentFormValues>;
                    return {
                      ...createEmptySuccessorAgent(),
                      ...candidate,
                      address: hydrateAddress(candidate.address),
                    };
                  },
                ),
              )
            : [],
        },
        authorityGrant: {
          powers: {
            ...defaultValues.authorityGrant.powers,
            ...(formValuesCandidate.authorityGrant?.powers ?? {}),
          },
          specialInstructions:
            formValuesCandidate.authorityGrant?.specialInstructions ??
            defaultValues.authorityGrant.specialInstructions,
        },
        effectiveAndTermination: {
          ...defaultValues.effectiveAndTermination,
          ...formValuesCandidate.effectiveAndTermination,
          effectiveDate:
            formValuesCandidate.effectiveAndTermination?.effectiveDate ===
              'immediate' ||
            formValuesCandidate.effectiveAndTermination?.effectiveDate ===
              'upon_incapacity'
              ? formValuesCandidate.effectiveAndTermination.effectiveDate
              : defaultValues.effectiveAndTermination.effectiveDate,
        },
      };
    } catch (error) {
      console.warn('Failed to parse stored draft', error);
      return createDefaultValues();
    }
  });
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>(
    {}
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlacesReady, setIsPlacesReady] = useState(false);
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const derivedIntake = useMemo<FinancialPOAIntake | null>(() => {
    try {
      const normalized = normalizeValues(values);
      return mapNormalizedToFinancialPoaIntake(normalized);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message !== 'effective_date_not_selected'
      ) {
        console.warn('Failed to derive financial POA intake', error);
      }
      return null;
    }
  }, [values]);

  const formContextValue = useMemo(
    () => ({ intake: derivedIntake }),
    [derivedIntake],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const draft: StoredDraft = {
      version: DRAFT_VERSION,
      formValues: values,
    };

    if (derivedIntake) {
      draft.intake = derivedIntake;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch (error) {
      console.warn('Failed to persist financial POA draft', error);
    }
  }, [values, derivedIntake]);

  useEffect(() => {
    const state = location.state as { focusRequest?: unknown } | null;

    if (!state?.focusRequest || !isFocusRequest(state.focusRequest)) {
      return;
    }

    const request = state.focusRequest;
    const targetIndex = stepDefinitions.findIndex(
      (step) => step.id === request.stepId,
    );

    if (targetIndex >= 0) {
      focusRequestRef.current = request;
      setSubmitAttempted(false);
      setCurrentStep(targetIndex);
    }

    navigate(location.pathname, { replace: true });
  }, [location, navigate]);

  useEffect(() => {
    const request = focusRequestRef.current;
    const activeStep = stepDefinitions[currentStep];

    if (!request || !activeStep || activeStep.id !== request.stepId) {
      return;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      focusRequestRef.current = null;
      return;
    }

    const attemptFocus = () => {
      const element = document.querySelector<HTMLElement>(request.selector);
      if (element) {
        element.focus();
        focusRequestRef.current = null;
        return true;
      }
      return false;
    };

    if (attemptFocus()) {
      return;
    }

    let frameId: number | null = null;
    let timeoutId: number | null = null;

    frameId = window.requestAnimationFrame(() => {
      if (attemptFocus()) {
        return;
      }

      timeoutId = window.setTimeout(() => {
        if (!attemptFocus()) {
          focusRequestRef.current = null;
        }
      }, 150);
    });

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [currentStep]);

  useEffect(() => {
    if (!hasInitializedStepRef.current) {
      hasInitializedStepRef.current = true;
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const container = stepContainerRef.current;

    if (!container) {
      return;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    container.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [currentStep]);

  useEffect(() => {
    let isCancelled = false;

    if (!googleMapsApiKey) {
      setIsPlacesReady(false);
      return;
    }

    setIsPlacesReady(false);

    loadGoogleMapsPlacesScript(googleMapsApiKey)
      .then(() => {
        if (!isCancelled) {
          setIsPlacesReady(true);
        }
      })
      .catch((error) => {
        console.error('Failed to load Google Maps Places script', error);
      });

    return () => {
      isCancelled = true;
    };
  }, [googleMapsApiKey]);

  const EFFECTIVE_DATE_ERROR_MESSAGE =
    'Select when the document should become effective.';

  const validationResult = useMemo(() => {
    const effectiveDate = values.effectiveAndTermination.effectiveDate;
    const hasValidEffectiveDate =
      effectiveDate === 'immediate' || effectiveDate === 'upon_incapacity';

    if (!hasValidEffectiveDate) {
      return {
        success: false,
        error: new z.ZodError([
          {
            code: z.ZodIssueCode.custom,
            path: ['effectiveAndTermination', 'effectiveDate'],
            message: EFFECTIVE_DATE_ERROR_MESSAGE,
          },
        ]),
      } as ReturnType<typeof financialPoaSchema.safeParse>;
    }

    try {
      return financialPoaSchema.safeParse(normalizeValues(values));
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === 'effective_date_not_selected'
      ) {
        return {
          success: false,
          error: new z.ZodError([
            {
              code: z.ZodIssueCode.custom,
              path: ['effectiveAndTermination', 'effectiveDate'],
              message: EFFECTIVE_DATE_ERROR_MESSAGE,
            },
          ]),
        } as ReturnType<typeof financialPoaSchema.safeParse>;
      }

      throw error;
    }
  }, [values]);

  const errorMap = useMemo(() => {
    if (validationResult.success) {
      return {} as Partial<Record<FieldName, string>>;
    }

    const map: Partial<Record<FieldName, string>> = {};
    for (const issue of validationResult.error.issues) {
      const path = issue.path.join('.');
      if (path && !map[path as FieldName]) {
        map[path as FieldName] = issue.message;
      }
    }

    return map;
  }, [validationResult]);

  const isFormValid = validationResult.success;

  const pendingCorrections = useMemo<StepCorrectionSummary[]>(() => {
    if (isFormValid) {
      return [];
    }

    return stepDefinitions
      .map<StepCorrectionSummary | null>((step) => {
        const fields = stepFieldGroups[step.id](values);
        const items = fields
          .map((field) => {
            const message = errorMap[field];
            if (!message) {
              return null;
            }

            return {
              field,
              label: getFieldLabel(field),
              message,
            };
          })
          .filter(Boolean) as StepCorrectionSummary['items'];

        if (items.length === 0) {
          return null;
        }

        return {
          stepId: step.id,
          stepLabel: step.label,
          items,
        };
      })
      .filter((summary): summary is StepCorrectionSummary => Boolean(summary));
  }, [errorMap, isFormValid, values]);

  const markFieldsTouched = useCallback(
    (fields: FieldName[]) => {
      if (fields.length === 0) {
        return;
      }

      setTouched((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = true;
        });
        return next;
      });
    },
    [setTouched],
  );

  const mergeAddressValues = useCallback(
    (
      current: AddressFormValues,
      selection: ParsedPlaceAddress,
    ): AddressFormValues => {
      let state = current.state;
      if (selection.state && isStateCode(selection.state)) {
        state = selection.state;
      }

      return {
        line1: selection.line1 ?? current.line1,
        line2: selection.line2 ?? current.line2,
        city: selection.city ?? current.city,
        state,
        postalCode: selection.postalCode ?? current.postalCode,
      };
    },
    [],
  );

  const handlePrincipalInfoChange =
    <Key extends Exclude<keyof PrincipalInfoFormValues, 'address'>>(key: Key) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setValues((prev) => ({
        ...prev,
        principalInfo: {
          ...prev.principalInfo,
          [key]: value,
        },
      }));
    };

  const handlePrincipalAddressChange =
    (key: keyof AddressFormValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setValues((prev) => ({
        ...prev,
        principalInfo: {
          ...prev.principalInfo,
          address: {
            ...prev.principalInfo.address,
            [key]: value,
          },
        },
      }));
    };

  const handleAgentInfoChange =
    <Key extends Exclude<keyof AgentInfoFormValues, 'agentAddress' | 'successorAgents' | 'includeSuccessorAgents'>>(key: Key) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setValues((prev) => ({
        ...prev,
        agentInfo: {
          ...prev.agentInfo,
          [key]: value,
        },
      }));
    };

  const handleAgentAddressChange =
    (key: keyof AddressFormValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setValues((prev) => ({
        ...prev,
        agentInfo: {
          ...prev.agentInfo,
          agentAddress: {
            ...prev.agentInfo.agentAddress,
            [key]: value,
          },
        },
      }));
    };

  const handleIncludeSuccessorAgentsChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    setValues((prev) => {
      const nextAgents = checked
        ? clampSuccessorAgents(
            prev.agentInfo.successorAgents.length > 0
              ? prev.agentInfo.successorAgents
              : [createEmptySuccessorAgent()],
          )
        : [];

      return {
        ...prev,
        agentInfo: {
          ...prev.agentInfo,
          includeSuccessorAgents: checked,
          successorAgents: nextAgents,
        },
      };
    });

    if (!checked) {
      setTouched((prev) => {
        const next: Partial<Record<FieldName, boolean>> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (!key.startsWith('agentInfo.successorAgents')) {
            next[key as FieldName] = value;
          }
        });
        return next;
      });
    }
  };

  const handleSuccessorAgentChange =
    <Key extends Exclude<keyof SuccessorAgentFormValues, 'address'>>(
      index: number,
      key: Key,
    ) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setValues((prev) => {
        const nextAgents = prev.agentInfo.successorAgents.map((agent, idx) =>
          idx === index
            ? {
                ...agent,
                [key]: value,
              }
            : agent
        );

        return {
          ...prev,
          agentInfo: {
            ...prev.agentInfo,
            successorAgents: nextAgents,
          },
        };
      });
    };

  const handleSuccessorAgentAddressChange =
    (index: number, key: keyof AddressFormValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setValues((prev) => {
        const nextAgents = prev.agentInfo.successorAgents.map((agent, idx) =>
          idx === index
            ? {
                ...agent,
                address: {
                  ...agent.address,
                  [key]: value,
                },
              }
            : agent
        );

        return {
          ...prev,
          agentInfo: {
            ...prev.agentInfo,
            successorAgents: nextAgents,
          },
        };
      });
    };

  const handlePrincipalAddressAutocomplete = useCallback(
    (selection: ParsedPlaceAddress) => {
      setValues((prev) => ({
        ...prev,
        principalInfo: {
          ...prev.principalInfo,
          address: mergeAddressValues(prev.principalInfo.address, selection),
        },
      }));

      markFieldsTouched([
        'principalInfo.address.line1',
        'principalInfo.address.city',
        'principalInfo.address.state',
        'principalInfo.address.postalCode',
      ]);
    },
    [markFieldsTouched, mergeAddressValues],
  );

  const handleAgentAddressAutocomplete = useCallback(
    (selection: ParsedPlaceAddress) => {
      setValues((prev) => ({
        ...prev,
        agentInfo: {
          ...prev.agentInfo,
          agentAddress: mergeAddressValues(
            prev.agentInfo.agentAddress,
            selection,
          ),
        },
      }));

      markFieldsTouched([
        'agentInfo.agentAddress.line1',
        'agentInfo.agentAddress.city',
        'agentInfo.agentAddress.state',
        'agentInfo.agentAddress.postalCode',
      ]);
    },
    [markFieldsTouched, mergeAddressValues],
  );

  const handleSuccessorAgentAddressAutocomplete = useCallback(
    (index: number, selection: ParsedPlaceAddress) => {
      setValues((prev) => {
        const nextAgents = prev.agentInfo.successorAgents.map((agent, idx) =>
          idx === index
            ? {
                ...agent,
                address: mergeAddressValues(agent.address, selection),
              }
            : agent,
        );

        return {
          ...prev,
          agentInfo: {
            ...prev.agentInfo,
            successorAgents: nextAgents,
          },
        };
      });

      markFieldsTouched([
        `agentInfo.successorAgents.${index}.address.line1`,
        `agentInfo.successorAgents.${index}.address.city`,
        `agentInfo.successorAgents.${index}.address.state`,
        `agentInfo.successorAgents.${index}.address.postalCode`,
      ]);
    },
    [markFieldsTouched, mergeAddressValues],
  );

  const handleAddSuccessorAgent = () => {
    setValues((prev) => {
      if (prev.agentInfo.successorAgents.length >= MAX_SUCCESSOR_AGENTS) {
        return prev;
      }

      return {
        ...prev,
        agentInfo: {
          ...prev.agentInfo,
          successorAgents: [
            ...prev.agentInfo.successorAgents,
            createEmptySuccessorAgent(),
          ],
        },
      };
    });
  };

  const handleRemoveSuccessorAgent = (index: number) => {
    setValues((prev) => ({
      ...prev,
      agentInfo: {
        ...prev.agentInfo,
        successorAgents: prev.agentInfo.successorAgents.filter(
          (_, idx) => idx !== index
        ),
      },
    }));

    setTouched((prev) => {
      const next: Partial<Record<FieldName, boolean>> = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (!key.startsWith('agentInfo.successorAgents.')) {
          next[key as FieldName] = value;
          return;
        }

        const match = key.match(
          /^agentInfo\.successorAgents\.(\d+)\.(.+)$/
        );
        if (!match) {
          next[key as FieldName] = value;
          return;
        }

        const idx = Number(match[1]);
        const rest = match[2];
        if (idx === index) {
          return;
        }

        const newKey =
          idx > index
            ? (`agentInfo.successorAgents.${idx - 1}.${rest}` as FieldName)
            : (key as FieldName);
        next[newKey] = value;
      });
      return next;
    });
  };

  const handleSelectAllPowers = () => {
    setValues((prev) => {
      const updatedPowers = powerOptions.reduce<Record<PowerKey, boolean>>(
        (acc, option) => {
          acc[option.key] = true;
          return acc;
        },
        { ...prev.authorityGrant.powers },
      );

      return {
        ...prev,
        authorityGrant: {
          ...prev.authorityGrant,
          powers: updatedPowers,
        },
      };
    });
  };

  const handlePowerChange =
    (key: PowerKey) => (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setValues((prev) => ({
        ...prev,
        authorityGrant: {
          ...prev.authorityGrant,
          powers: {
            ...prev.authorityGrant.powers,
            [key]: checked,
          },
        },
      }));
    };

  const handleSpecialInstructionsChange = (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setValues((prev) => ({
      ...prev,
      authorityGrant: {
        ...prev.authorityGrant,
        specialInstructions: value,
      },
    }));
  };

  const handleEffectiveDateChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value as EffectiveDateOption | '';
    setValues((prev) => ({
      ...prev,
      effectiveAndTermination: {
        ...prev.effectiveAndTermination,
        effectiveDate: value,
      },
    }));
  };

  const handleTerminationConditionsChange = (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setValues((prev) => ({
      ...prev,
      effectiveAndTermination: {
        ...prev.effectiveAndTermination,
        terminationConditions: value,
      },
    }));
  };

  const handleNextStep = (event?: MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (currentStep >= stepDefinitions.length - 1) {
      return;
    }

    setSubmitAttempted(false);
    const stepId = stepDefinitions[currentStep].id;
    const fields = stepFieldGroups[stepId](values);
    markFieldsTouched(fields);

    const stepIsValid = fields.every((field) => !errorMap[field]);
    if (stepIsValid) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleStepSelect = (index: number) => {
    setSubmitAttempted(false);
    setCurrentStep(Math.max(0, Math.min(index, stepDefinitions.length - 1)));
  };

  const handlePreviousStep = () => {
    if (currentStep === 0) {
      return;
    }

    setSubmitAttempted(false);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleBlur = (field: FieldName) => () => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const allPowersSelected = useMemo(
    () =>
      powerOptions.every(
        (option) => values.authorityGrant.powers[option.key],
      ),
    [values.authorityGrant.powers],
  );

  const getFieldError = (field: FieldName) => {
    if (!submitAttempted && !touched[field]) {
      return undefined;
    }

    return errorMap[field];
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (currentStep < stepDefinitions.length - 1) {
      handleNextStep();
      return;
    }

    setSubmitAttempted(true);
    const allFields = getAllFieldNames(values);
    markFieldsTouched(allFields);

    if (isFormValid) {
      if (typeof window !== 'undefined') {
        try {
          const normalized = normalizeValues(values);
          const intake = mapNormalizedToFinancialPoaIntake(normalized);
          const draft: StoredDraft = {
            version: DRAFT_VERSION,
            formValues: values,
            intake,
          };
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
        } catch (error) {
          console.warn(
            'Failed to store financial POA intake before review',
            error,
          );
        }
      }

      navigate('/poa/financial/review');
      return;
    }

    const firstInvalidStepIndex = stepDefinitions.findIndex((step) => {
      const fields = stepFieldGroups[step.id](values);
      return fields.some((field) => errorMap[field]);
    });

    if (firstInvalidStepIndex >= 0) {
      setCurrentStep(firstInvalidStepIndex);
    }
  };

  const handleClearDraft = () => {
    setValues(createDefaultValues());
    setTouched({});
    setSubmitAttempted(false);
    setCurrentStep(0);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  };

  const activeStep = stepDefinitions[currentStep];
  const isLastStep = currentStep === stepDefinitions.length - 1;
  const primaryActionLabel = isLastStep
    ? 'Review & continue →'
    : `Next: ${stepDefinitions[currentStep + 1].label} →`;

  const powerSelectionError = getFieldError('authorityGrant.powers');
  const successorAgentsError = getFieldError('agentInfo.successorAgents');
  const effectiveDateError = getFieldError(
    'effectiveAndTermination.effectiveDate'
  );
  const terminationConditionsError = getFieldError(
    'effectiveAndTermination.terminationConditions'
  );

  return (
    <FinancialPoaFormContext.Provider value={formContextValue}>
      <div className={styles.wrapper}>
        <div className={styles.layout}>
          <main className={styles.main} ref={stepContainerRef}>
          <nav aria-label="Document navigation" className={styles.backNav}>
            <Link to="/" className={styles.backLink}>
              ← Back to Document List
            </Link>
          </nav>
          <header className={styles.header}>
            <div>
              <h1 className={styles.heading}>
                Financial Power of Attorney (Maine)
              </h1>
              <p className={styles.subheading}>
                Enter information to generate your document.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClearDraft}
              className={styles.clearButton}
            >
              Clear Draft
            </button>
          </header>

          <div className={styles.stepper} role="list" aria-label="Form steps">
            {stepDefinitions.map((step, index) => {
              const isActive = index === currentStep;
              const statusClass =
                index < currentStep
                  ? styles.stepComplete
                  : isActive
                  ? styles.stepActive
                  : '';
              const subLabelClassName = `${styles.stepSubLabel} ${
                isActive ? styles.stepSubLabelActive : ''
              }`.trim();

              return (
                <button
                  key={step.id}
                  className={`${styles.step} ${statusClass}`.trim()}
                  type="button"
                  onClick={() => handleStepSelect(index)}
                  role="listitem"
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span className={styles.stepNumber}>{index + 1}</span>
                  <div className={styles.stepText}>
                    <span className={styles.stepLabel}>{step.label}</span>
                    <span className={subLabelClassName}>{step.title}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.stepHeader}>
              <div className={styles.stepHeaderIntro}>
                <span className={styles.stepBadge}>{`Step ${currentStep + 1} of ${stepDefinitions.length}`}</span>
                <h2 className={styles.stepHeading}>{activeStep.title}</h2>
                <p className={styles.stepDescription}>{activeStep.description}</p>
              </div>
              {activeStep.preview.length > 0 && (
                <div className={styles.stepPreview}>
                  <p className={styles.stepPreviewLabel}>You'll need:</p>
                  <ul className={styles.stepPreviewList}>
                    {activeStep.preview.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {activeStep.id === 'principal_info' && (
              <div className={styles.stepContent}>
                <p className={styles.stepNote}>
                  Use your legal name and current contact information. These
                  details appear on the finished document so banks and agencies
                  know exactly who is granting the authority.
                </p>
                <div className={styles.stepSections}>
                  <ContactSection
                    title="Your contact details"
                    description="We'll show this information at the top of your document."
                  >
                    <div className={styles.fieldGrid}>
                      <div
                        className={`${styles.field} ${styles.fieldFullWidth}`}
                      >
                        <label htmlFor="principal-full-name">
                          Full legal name
                        </label>
                        <input
                          id="principal-full-name"
                          name="principalFullName"
                          type="text"
                          value={values.principalInfo.fullName}
                          onChange={handlePrincipalInfoChange('fullName')}
                          onBlur={handleBlur('principalInfo.fullName')}
                          aria-invalid={
                            getFieldError('principalInfo.fullName')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="principal-full-name-error"
                          autoComplete="name"
                        />
                        {getFieldError('principalInfo.fullName') && (
                          <p
                            id="principal-full-name-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('principalInfo.fullName')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="principal-phone">Phone number</label>
                        <input
                          id="principal-phone"
                          name="principalPhone"
                          type="tel"
                          value={values.principalInfo.phone}
                          onChange={handlePrincipalInfoChange('phone')}
                          onBlur={handleBlur('principalInfo.phone')}
                          aria-invalid={
                            getFieldError('principalInfo.phone')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="principal-phone-error"
                          autoComplete="tel"
                        />
                        {getFieldError('principalInfo.phone') && (
                          <p
                            id="principal-phone-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('principalInfo.phone')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="principal-email">
                          Email <OptionalIndicator />
                        </label>
                        <input
                          id="principal-email"
                          name="principalEmail"
                          type="email"
                          value={values.principalInfo.email}
                          onChange={handlePrincipalInfoChange('email')}
                          onBlur={handleBlur('principalInfo.email')}
                          aria-invalid={
                            getFieldError('principalInfo.email')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="principal-email-error"
                          autoComplete="email"
                        />
                        {getFieldError('principalInfo.email') && (
                          <p
                            id="principal-email-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('principalInfo.email')}
                          </p>
                        )}
                      </div>
                    </div>
                  </ContactSection>

                  <AddressGroup
                    title="Home address"
                    description="Helps banks and agencies verify your identity."
                  >
                    <div className={styles.fieldGrid}>
                      <div
                        className={`${styles.field} ${styles.fieldFullWidth}`}
                      >
                        <label htmlFor="principal-address-line1">
                          Street address
                        </label>
                        <GoogleAddressInput
                          id="principal-address-line1"
                          name="principalAddressLine1"
                          type="text"
                          value={values.principalInfo.address.line1}
                          onChange={handlePrincipalAddressChange('line1')}
                          onBlur={handleBlur('principalInfo.address.line1')}
                          aria-invalid={
                            getFieldError('principalInfo.address.line1')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="principal-address-line1-error"
                          autoComplete="address-line1"
                          autocompleteEnabled={isPlacesReady}
                          onAddressSelect={handlePrincipalAddressAutocomplete}
                        />
                        {getFieldError('principalInfo.address.line1') && (
                          <p
                            id="principal-address-line1-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('principalInfo.address.line1')}
                          </p>
                        )}
                      </div>

                      <div
                        className={`${styles.field} ${styles.fieldFullWidth}`}
                      >
                        <label htmlFor="principal-address-line2">
                          Apartment, suite, etc. <OptionalIndicator />
                        </label>
                        <input
                          id="principal-address-line2"
                          name="principalAddressLine2"
                          type="text"
                          value={values.principalInfo.address.line2}
                          onChange={handlePrincipalAddressChange('line2')}
                          onBlur={handleBlur('principalInfo.address.line2')}
                          autoComplete="address-line2"
                        />
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="principal-city">City</label>
                        <input
                          id="principal-city"
                          name="principalCity"
                          type="text"
                          value={values.principalInfo.address.city}
                          onChange={handlePrincipalAddressChange('city')}
                          onBlur={handleBlur('principalInfo.address.city')}
                          aria-invalid={
                            getFieldError('principalInfo.address.city')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="principal-city-error"
                          autoComplete="address-level2"
                        />
                        {getFieldError('principalInfo.address.city') && (
                          <p
                            id="principal-city-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('principalInfo.address.city')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="principal-state">State</label>
                        <select
                          id="principal-state"
                          name="principalState"
                          value={values.principalInfo.address.state}
                          onChange={handlePrincipalAddressChange('state')}
                          onBlur={handleBlur('principalInfo.address.state')}
                          aria-invalid={
                            getFieldError('principalInfo.address.state')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="principal-state-error"
                          autoComplete="address-level1"
                        >
                          {STATE_OPTIONS.map((state) => (
                            <option key={state.value} value={state.value}>
                              {state.label}
                            </option>
                          ))}
                        </select>
                        {getFieldError('principalInfo.address.state') && (
                          <p
                            id="principal-state-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('principalInfo.address.state')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="principal-postal-code">ZIP code</label>
                        <input
                          id="principal-postal-code"
                          name="principalPostalCode"
                          type="text"
                          inputMode="numeric"
                          value={values.principalInfo.address.postalCode}
                          onChange={handlePrincipalAddressChange('postalCode')}
                          onBlur={handleBlur('principalInfo.address.postalCode')}
                          aria-invalid={
                            getFieldError('principalInfo.address.postalCode')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="principal-postal-code-error"
                          autoComplete="postal-code"
                        />
                        {getFieldError('principalInfo.address.postalCode') && (
                          <p
                            id="principal-postal-code-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('principalInfo.address.postalCode')}
                          </p>
                        )}
                      </div>
                    </div>
                  </AddressGroup>
                </div>
              </div>
            )}

            {activeStep.id === 'agent_info' && (
              <div className={styles.stepContent}>
                <p className={styles.stepNote}>
                  Provide complete contact details for the person you trust to
                  act on your behalf. Accurate information helps financial
                  institutions confirm their identity and reach them quickly.
                </p>
                <div className={styles.stepSections}>
                  <ContactSection
                    title="Primary agent contact"
                    description="Tell us who will represent you and how to reach them."
                    icon={FaHandsHelping}
                  >
                    <div className={styles.fieldGrid}>
                      <div
                        className={`${styles.field} ${styles.fieldFullWidth}`}
                      >
                        <label htmlFor="agent-full-name">
                          Agent full legal name
                        </label>
                        <input
                          id="agent-full-name"
                          name="agentFullName"
                          type="text"
                          value={values.agentInfo.agentFullName}
                          onChange={handleAgentInfoChange('agentFullName')}
                          onBlur={handleBlur('agentInfo.agentFullName')}
                          aria-invalid={
                            getFieldError('agentInfo.agentFullName')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="agent-full-name-error"
                          autoComplete="name"
                        />
                        {getFieldError('agentInfo.agentFullName') && (
                          <p
                            id="agent-full-name-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('agentInfo.agentFullName')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="agent-phone">Agent phone number</label>
                        <input
                          id="agent-phone"
                          name="agentPhone"
                          type="tel"
                          value={values.agentInfo.agentPhone}
                          onChange={handleAgentInfoChange('agentPhone')}
                          onBlur={handleBlur('agentInfo.agentPhone')}
                          aria-invalid={
                            getFieldError('agentInfo.agentPhone') ? 'true' : 'false'
                          }
                          aria-describedby="agent-phone-error"
                          autoComplete="tel"
                        />
                        {getFieldError('agentInfo.agentPhone') && (
                          <p
                            id="agent-phone-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('agentInfo.agentPhone')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="agent-email">
                          Agent email <OptionalIndicator />
                        </label>
                        <input
                          id="agent-email"
                          name="agentEmail"
                          type="email"
                          value={values.agentInfo.agentEmail}
                          onChange={handleAgentInfoChange('agentEmail')}
                          onBlur={handleBlur('agentInfo.agentEmail')}
                          aria-invalid={
                            getFieldError('agentInfo.agentEmail')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="agent-email-error"
                          autoComplete="email"
                        />
                        {getFieldError('agentInfo.agentEmail') && (
                          <p
                            id="agent-email-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('agentInfo.agentEmail')}
                          </p>
                        )}
                      </div>
                    </div>
                  </ContactSection>

                  <AddressGroup
                    title="Where your agent lives"
                    description="Institutions use this to verify the right person."
                  >
                    <div className={styles.fieldGrid}>
                      <div
                        className={`${styles.field} ${styles.fieldFullWidth}`}
                      >
                        <label htmlFor="agent-address-line1">
                          Agent street address
                        </label>
                        <GoogleAddressInput
                          id="agent-address-line1"
                          name="agentAddressLine1"
                          type="text"
                          value={values.agentInfo.agentAddress.line1}
                          onChange={handleAgentAddressChange('line1')}
                          onBlur={handleBlur('agentInfo.agentAddress.line1')}
                          aria-invalid={
                            getFieldError('agentInfo.agentAddress.line1')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="agent-address-line1-error"
                          autoComplete="address-line1"
                          autocompleteEnabled={isPlacesReady}
                          onAddressSelect={handleAgentAddressAutocomplete}
                        />
                        {getFieldError('agentInfo.agentAddress.line1') && (
                          <p
                            id="agent-address-line1-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('agentInfo.agentAddress.line1')}
                          </p>
                        )}
                      </div>

                      <div
                        className={`${styles.field} ${styles.fieldFullWidth}`}
                      >
                        <label htmlFor="agent-address-line2">
                          Apartment, suite, etc. <OptionalIndicator />
                        </label>
                        <input
                          id="agent-address-line2"
                          name="agentAddressLine2"
                          type="text"
                          value={values.agentInfo.agentAddress.line2}
                          onChange={handleAgentAddressChange('line2')}
                          onBlur={handleBlur('agentInfo.agentAddress.line2')}
                          autoComplete="address-line2"
                        />
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="agent-city">City</label>
                        <input
                          id="agent-city"
                          name="agentCity"
                          type="text"
                          value={values.agentInfo.agentAddress.city}
                          onChange={handleAgentAddressChange('city')}
                          onBlur={handleBlur('agentInfo.agentAddress.city')}
                          aria-invalid={
                            getFieldError('agentInfo.agentAddress.city')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="agent-city-error"
                          autoComplete="address-level2"
                        />
                        {getFieldError('agentInfo.agentAddress.city') && (
                          <p
                            id="agent-city-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('agentInfo.agentAddress.city')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="agent-state">State</label>
                        <select
                          id="agent-state"
                          name="agentState"
                          value={values.agentInfo.agentAddress.state}
                          onChange={handleAgentAddressChange('state')}
                          onBlur={handleBlur('agentInfo.agentAddress.state')}
                          aria-invalid={
                            getFieldError('agentInfo.agentAddress.state')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="agent-state-error"
                          autoComplete="address-level1"
                        >
                          {STATE_OPTIONS.map((state) => (
                            <option key={state.value} value={state.value}>
                              {state.label}
                            </option>
                          ))}
                        </select>
                        {getFieldError('agentInfo.agentAddress.state') && (
                          <p
                            id="agent-state-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('agentInfo.agentAddress.state')}
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <label htmlFor="agent-postal-code">ZIP code</label>
                        <input
                          id="agent-postal-code"
                          name="agentPostalCode"
                          type="text"
                          inputMode="numeric"
                          value={values.agentInfo.agentAddress.postalCode}
                          onChange={handleAgentAddressChange('postalCode')}
                          onBlur={handleBlur('agentInfo.agentAddress.postalCode')}
                          aria-invalid={
                            getFieldError('agentInfo.agentAddress.postalCode')
                              ? 'true'
                              : 'false'
                          }
                          aria-describedby="agent-postal-code-error"
                          autoComplete="postal-code"
                        />
                        {getFieldError('agentInfo.agentAddress.postalCode') && (
                          <p
                            id="agent-postal-code-error"
                            className={styles.error}
                            role="alert"
                          >
                            {getFieldError('agentInfo.agentAddress.postalCode')}
                          </p>
                        )}
                      </div>
                    </div>
                  </AddressGroup>

                  <SectionCard
                    title="Successor agents (optional)"
                    description="Add backups who can serve if your primary agent is unavailable."
                    icon={FaUserFriends}
                  >
                    <div className={styles.toggleRow}>
                      <div className={styles.toggleControl}>
                        <input
                          id="include-successor-agents"
                          type="checkbox"
                          checked={values.agentInfo.includeSuccessorAgents}
                          onChange={handleIncludeSuccessorAgentsChange}
                          aria-describedby="include-successor-agents-help"
                        />
                        <label
                          className={styles.toggleLabel}
                          htmlFor="include-successor-agents"
                        >
                          Add successor agent(s)
                        </label>
                      </div>
                      <p
                        id="include-successor-agents-help"
                        className={styles.toggleDescription}
                      >
                        Add up to two backup agents in case your primary agent is
                        unable or unwilling to serve when needed.
                      </p>
                    </div>

                    {values.agentInfo.includeSuccessorAgents && successorAgentsError && (
                      <p className={styles.error} role="alert">
                        {successorAgentsError}
                      </p>
                    )}

                    {values.agentInfo.includeSuccessorAgents && (
                      <div className={styles.successorList}>
                        {values.agentInfo.successorAgents.map((agent, index) => (
                          <section key={index} className={styles.successorCard}>
                            <div className={styles.successorHeader}>
                              <h3 className={styles.successorTitle}>
                                {`Successor Agent ${index + 1}`}
                              </h3>
                              {values.agentInfo.successorAgents.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSuccessorAgent(index)}
                                  className={styles.removeButton}
                                  aria-label={`Remove successor agent ${index + 1}`}
                                >
                                  Remove
                                </button>
                              )}
                            </div>

                            <div className={styles.fieldGrid}>
                          <div
                            className={`${styles.field} ${styles.fieldFullWidth}`}
                          >
                            <label htmlFor={`successor-${index}-full-name`}>
                              Full legal name
                            </label>
                            <input
                              id={`successor-${index}-full-name`}
                              name={`successor-${index}-full-name`}
                              type="text"
                              value={agent.fullName}
                              onChange={handleSuccessorAgentChange(index, 'fullName')}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.fullName`
                              )}
                              aria-invalid={
                                getFieldError(
                                  `agentInfo.successorAgents.${index}.fullName`
                                )
                                  ? 'true'
                                  : 'false'
                              }
                              aria-describedby={`successor-${index}-full-name-error`}
                            />
                            {getFieldError(
                              `agentInfo.successorAgents.${index}.fullName`
                            ) && (
                              <p
                                id={`successor-${index}-full-name-error`}
                                className={styles.error}
                                role="alert"
                              >
                                {getFieldError(
                                  `agentInfo.successorAgents.${index}.fullName`
                                )}
                              </p>
                            )}
                          </div>

                          <div
                            className={`${styles.field} ${styles.fieldFullWidth}`}
                          >
                            <label htmlFor={`successor-${index}-address-line1`}>
                              Street address
                            </label>
                            <GoogleAddressInput
                              id={`successor-${index}-address-line1`}
                              name={`successor-${index}-address-line1`}
                              type="text"
                              value={agent.address.line1}
                              onChange={handleSuccessorAgentAddressChange(
                                index,
                                'line1',
                              )}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.address.line1`,
                              )}
                              aria-invalid={
                                getFieldError(
                                  `agentInfo.successorAgents.${index}.address.line1`,
                                )
                                  ? 'true'
                                  : 'false'
                              }
                              aria-describedby={`successor-${index}-address-line1-error`}
                              autoComplete="address-line1"
                              autocompleteEnabled={isPlacesReady}
                              onAddressSelect={(selection) =>
                                handleSuccessorAgentAddressAutocomplete(
                                  index,
                                  selection,
                                )
                              }
                            />
                            {getFieldError(
                              `agentInfo.successorAgents.${index}.address.line1`,
                            ) && (
                              <p
                                id={`successor-${index}-address-line1-error`}
                                className={styles.error}
                                role="alert"
                              >
                                {getFieldError(
                                  `agentInfo.successorAgents.${index}.address.line1`,
                                )}
                              </p>
                            )}
                          </div>

                          <div
                            className={`${styles.field} ${styles.fieldFullWidth}`}
                          >
                            <label htmlFor={`successor-${index}-address-line2`}>
                              Apartment, suite, etc. <OptionalIndicator />
                            </label>
                            <input
                              id={`successor-${index}-address-line2`}
                              name={`successor-${index}-address-line2`}
                              type="text"
                              value={agent.address.line2}
                              onChange={handleSuccessorAgentAddressChange(
                                index,
                                'line2',
                              )}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.address.line2`,
                              )}
                              autoComplete="address-line2"
                            />
                          </div>

                          <div className={styles.field}>
                            <label htmlFor={`successor-${index}-city`}>
                              City
                            </label>
                            <input
                              id={`successor-${index}-city`}
                              name={`successor-${index}-city`}
                              type="text"
                              value={agent.address.city}
                              onChange={handleSuccessorAgentAddressChange(
                                index,
                                'city',
                              )}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.address.city`,
                              )}
                              aria-invalid={
                                getFieldError(
                                  `agentInfo.successorAgents.${index}.address.city`,
                                )
                                  ? 'true'
                                  : 'false'
                              }
                              aria-describedby={`successor-${index}-city-error`}
                              autoComplete="address-level2"
                            />
                            {getFieldError(
                              `agentInfo.successorAgents.${index}.address.city`,
                            ) && (
                              <p
                                id={`successor-${index}-city-error`}
                                className={styles.error}
                                role="alert"
                              >
                                {getFieldError(
                                  `agentInfo.successorAgents.${index}.address.city`,
                                )}
                              </p>
                            )}
                          </div>

                          <div className={styles.field}>
                            <label htmlFor={`successor-${index}-state`}>
                              State
                            </label>
                            <select
                              id={`successor-${index}-state`}
                              name={`successor-${index}-state`}
                              value={agent.address.state}
                              onChange={handleSuccessorAgentAddressChange(
                                index,
                                'state',
                              )}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.address.state`,
                              )}
                              aria-invalid={
                                getFieldError(
                                  `agentInfo.successorAgents.${index}.address.state`,
                                )
                                  ? 'true'
                                  : 'false'
                              }
                              aria-describedby={`successor-${index}-state-error`}
                              autoComplete="address-level1"
                            >
                              {STATE_OPTIONS.map((state) => (
                                <option key={state.value} value={state.value}>
                                  {state.label}
                                </option>
                              ))}
                            </select>
                            {getFieldError(
                              `agentInfo.successorAgents.${index}.address.state`,
                            ) && (
                              <p
                                id={`successor-${index}-state-error`}
                                className={styles.error}
                                role="alert"
                              >
                                {getFieldError(
                                  `agentInfo.successorAgents.${index}.address.state`,
                                )}
                              </p>
                            )}
                          </div>

                          <div className={styles.field}>
                            <label htmlFor={`successor-${index}-postal-code`}>
                              ZIP code
                            </label>
                            <input
                              id={`successor-${index}-postal-code`}
                              name={`successor-${index}-postal-code`}
                              type="text"
                              inputMode="numeric"
                              value={agent.address.postalCode}
                              onChange={handleSuccessorAgentAddressChange(
                                index,
                                'postalCode',
                              )}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.address.postalCode`,
                              )}
                              aria-invalid={
                                getFieldError(
                                  `agentInfo.successorAgents.${index}.address.postalCode`,
                                )
                                  ? 'true'
                                  : 'false'
                              }
                              aria-describedby={`successor-${index}-postal-code-error`}
                              autoComplete="postal-code"
                            />
                            {getFieldError(
                              `agentInfo.successorAgents.${index}.address.postalCode`,
                            ) && (
                              <p
                                id={`successor-${index}-postal-code-error`}
                                className={styles.error}
                                role="alert"
                              >
                                {getFieldError(
                                  `agentInfo.successorAgents.${index}.address.postalCode`,
                                )}
                              </p>
                            )}
                          </div>

                          <div className={styles.field}>
                            <label htmlFor={`successor-${index}-phone`}>
                              Phone number
                            </label>
                            <input
                              id={`successor-${index}-phone`}
                              name={`successor-${index}-phone`}
                              type="tel"
                              value={agent.phone}
                              onChange={handleSuccessorAgentChange(index, 'phone')}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.phone`
                              )}
                              aria-invalid={
                                getFieldError(
                                  `agentInfo.successorAgents.${index}.phone`
                                )
                                  ? 'true'
                                  : 'false'
                              }
                              aria-describedby={`successor-${index}-phone-error`}
                              autoComplete="tel"
                            />
                            {getFieldError(
                              `agentInfo.successorAgents.${index}.phone`
                            ) && (
                              <p
                                id={`successor-${index}-phone-error`}
                                className={styles.error}
                                role="alert"
                              >
                                {getFieldError(
                                  `agentInfo.successorAgents.${index}.phone`
                                )}
                              </p>
                            )}
                          </div>

                          <div className={styles.field}>
                            <label htmlFor={`successor-${index}-email`}>
                              Email <OptionalIndicator />
                            </label>
                            <input
                              id={`successor-${index}-email`}
                              name={`successor-${index}-email`}
                              type="email"
                              value={agent.email}
                              onChange={handleSuccessorAgentChange(index, 'email')}
                              onBlur={handleBlur(
                                `agentInfo.successorAgents.${index}.email`
                              )}
                              aria-invalid={
                                getFieldError(
                                  `agentInfo.successorAgents.${index}.email`
                                )
                                  ? 'true'
                                  : 'false'
                              }
                              aria-describedby={`successor-${index}-email-error`}
                              autoComplete="email"
                            />
                            {getFieldError(
                              `agentInfo.successorAgents.${index}.email`
                            ) && (
                              <p
                                id={`successor-${index}-email-error`}
                                className={styles.error}
                                role="alert"
                              >
                                {getFieldError(
                                  `agentInfo.successorAgents.${index}.email`
                                )}
                              </p>
                            )}
                          </div>
                            </div>
                          </section>
                        ))}
                        {values.agentInfo.successorAgents.length <
                          MAX_SUCCESSOR_AGENTS && (
                          <button
                            type="button"
                            onClick={handleAddSuccessorAgent}
                            className={styles.addSuccessorButton}
                          >
                            + Add another successor agent
                          </button>
                        )}
                      </div>
                    )}
                  </SectionCard>
                </div>
              </div>
            )}

            {activeStep.id === 'authority_grant' && (
              <div className={styles.stepContent}>
                <p className={styles.stepNote}>
                  Decide which financial tasks your agent can manage. You can
                  select every card that matches the help you want from them.
                </p>
                <div className={styles.stepSections}>
                  <SectionCard
                    title="Choose the financial powers"
                    description="Pick every area you are comfortable delegating to your agent."
                    icon={FaBalanceScale}
                  >
                    <fieldset className={styles.fieldset}>
                      <legend className={styles.visuallyHidden}>
                        Choose the financial powers
                      </legend>
                      <div className={styles.helperRow}>
                        <p className={styles.helperText}>
                          Pick the areas you are comfortable delegating. Feel free
                          to choose more than one option.
                        </p>
                        <button
                          type="button"
                          onClick={handleSelectAllPowers}
                          className={styles.selectAllButton}
                          disabled={allPowersSelected}
                        >
                          Grant all powers
                        </button>
                      </div>
                      <div className={styles.cardGrid}>
                        {powerOptions.map((option) => {
                          const checked =
                            values.authorityGrant.powers[option.key];

                          return (
                            <label
                              key={option.key}
                              className={`${styles.selectableCard} ${
                                checked ? styles.selectableCardSelected : ''
                              }`.trim()}
                            >
                              {checked && (
                                <span className={styles.selectionIndicator}>
                                  <FaCheck aria-hidden="true" />
                                </span>
                              )}
                              <input
                                type="checkbox"
                                name={`power-${option.key}`}
                                checked={checked}
                                onChange={handlePowerChange(option.key)}
                                className={styles.visuallyHidden}
                              />
                              <option.Icon
                                aria-hidden="true"
                                className={styles.cardIcon}
                              />
                              <div className={styles.cardText}>
                                <span className={styles.cardTitle}>{option.label}</span>
                                <span className={styles.cardDescription}>
                                  {option.description}
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {powerSelectionError && (
                        <p className={styles.error} role="alert">
                          {powerSelectionError}
                        </p>
                      )}
                    </fieldset>
                  </SectionCard>

                  <SectionCard
                    title="Add limits or special instructions"
                    description="Share any guardrails or notes you want your agent to follow."
                    icon={FaFileInvoiceDollar}
                  >
                    <div className={`${styles.field} ${styles.fieldFullWidth}`}>
                      <label htmlFor="special-instructions">
                        Special instructions or limits <OptionalIndicator />
                      </label>
                      <textarea
                        id="special-instructions"
                        name="specialInstructions"
                        rows={4}
                        value={values.authorityGrant.specialInstructions}
                        onChange={handleSpecialInstructionsChange}
                        onBlur={handleBlur('authorityGrant.specialInstructions')}
                        aria-invalid={
                          getFieldError('authorityGrant.specialInstructions')
                            ? 'true'
                            : 'false'
                        }
                        aria-describedby="special-instructions-error"
                      />
                      {getFieldError('authorityGrant.specialInstructions') && (
                        <p
                          id="special-instructions-error"
                          className={styles.error}
                          role="alert"
                        >
                          {getFieldError('authorityGrant.specialInstructions')}
                        </p>
                      )}
                    </div>
                  </SectionCard>
                </div>
              </div>
            )}

            {activeStep.id === 'effective_and_termination' && (
              <div className={styles.stepContent}>
                <p className={styles.stepNote}>
                  Decide when your agent’s authority should start and whether
                  you want to set any limits on how long it lasts.
                </p>
                <div className={styles.stepSections}>
                  <SectionCard
                    title="When can your agent start helping?"
                    description="Choose the option that best matches how quickly you want them to act on your behalf."
                    icon={FaBolt}
                  >
                    <fieldset className={styles.fieldset}>
                      <legend className={styles.visuallyHidden}>
                        When can your agent start helping?
                      </legend>
                      <p className={styles.helperText}>
                        Choose the option that best matches how quickly you want
                        them to act on your behalf.
                      </p>
                      <div className={styles.cardGrid}>
                        {effectiveDateOptions.map((option) => {
                          const selected =
                            values.effectiveAndTermination.effectiveDate ===
                            option.value;

                          return (
                            <label
                              key={option.value}
                              className={`${styles.selectableCard} ${
                                selected ? styles.selectableCardSelected : ''
                              }`.trim()}
                            >
                              {selected && (
                                <span className={styles.selectionIndicator}>
                                  <FaCheck aria-hidden="true" />
                                </span>
                              )}
                              <input
                                type="radio"
                                name="effective-date"
                                value={option.value}
                                checked={selected}
                                onChange={handleEffectiveDateChange}
                                onBlur={handleBlur(
                                  'effectiveAndTermination.effectiveDate',
                                )}
                                className={styles.visuallyHidden}
                                aria-invalid={
                                  effectiveDateError ? 'true' : 'false'
                                }
                                aria-describedby={
                                  effectiveDateError
                                    ? 'effective-date-error'
                                    : undefined
                                }
                              />
                              <option.Icon
                                aria-hidden="true"
                                className={styles.cardIcon}
                              />
                              <div className={styles.cardText}>
                                <span className={styles.cardTitle}>{option.label}</span>
                                <span className={styles.cardDescription}>
                                  {option.description}
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {effectiveDateError && (
                        <p
                          id="effective-date-error"
                          className={styles.error}
                          role="alert"
                        >
                          {effectiveDateError}
                        </p>
                      )}
                    </fieldset>
                  </SectionCard>

                  <SectionCard
                    title="When should the authority end?"
                    description="Leave this blank to keep things open-ended or add specific triggers."
                    icon={FaClock}
                  >
                    <div className={`${styles.field} ${styles.fieldFullWidth}`}>
                      <label htmlFor="termination-conditions">
                        End date or conditions <OptionalIndicator />
                      </label>
                      <textarea
                        id="termination-conditions"
                        name="terminationConditions"
                        rows={4}
                        value={values.effectiveAndTermination.terminationConditions}
                        onChange={handleTerminationConditionsChange}
                        onBlur={handleBlur(
                          'effectiveAndTermination.terminationConditions',
                        )}
                        aria-invalid={
                          terminationConditionsError ? 'true' : 'false'
                        }
                        aria-describedby="termination-conditions-error"
                      />
                      {terminationConditionsError && (
                        <p
                          id="termination-conditions-error"
                          className={styles.error}
                          role="alert"
                        >
                          {terminationConditionsError}
                        </p>
                      )}
                    </div>
                  </SectionCard>
                </div>
              </div>
            )}

            {isLastStep && pendingCorrections.length > 0 && (
              <section
                className={styles.correctionNotice}
                aria-live={submitAttempted ? 'assertive' : 'polite'}
                role="status"
              >
                <h2 className={styles.correctionTitle}>
                  Finish these items to generate your document
                </h2>
                <ul className={styles.correctionList}>
                  {pendingCorrections.map((summary) => (
                    <li
                      key={summary.stepId}
                      className={styles.correctionListItem}
                    >
                      <p className={styles.correctionStepLabel}>
                        {summary.stepLabel}
                      </p>
                      <ul className={styles.correctionFieldList}>
                        {summary.items.map((item) => (
                          <li
                            key={item.field}
                            className={styles.correctionFieldItem}
                          >
                            <span className={styles.correctionFieldName}>
                              {item.label}
                            </span>
                            <span className={styles.correctionFieldMessage}>
                              {item.message}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className={styles.actions}>
              <div className={styles.secondaryGroup}>
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    className={styles.secondaryButton}
                  >
                    ← Previous step
                  </button>
                )}
              </div>
              <div className={styles.primaryGroup}>
                {!isFormValid && submitAttempted && (
                  <p className={styles.submitError} role="alert">
                    Please fix the highlighted fields to continue.
                  </p>
                )}
                {isLastStep ? (
                  <button
                    type="submit"
                    className={styles.primaryButton}
                    disabled={!isFormValid}
                  >
                    {primaryActionLabel}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={handleNextStep}
                  >
                    {primaryActionLabel}
                  </button>
                )}
              </div>
            </div>
          </form>
        </main>
      </div>
    </div>
  </FinancialPoaFormContext.Provider>
  );
};

export default FinancialPoaForm;
